import { inject, Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpInterceptor, HttpErrorResponse, HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, finalize } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { datadogLogs } from '@datadog/browser-logs';

import { SpinnerService } from '../layout/spinner/spinner.service';
import { SecurityService } from '../security/security.service';
import { NotificationService } from '../../services/notification.service';
import { profileIsViewOnly } from '../security/permission-profiles';
import { InactivityTimeoutService } from '../inactivity-timeout/inactivity-timeout.service';

@Injectable()
export class HttpClientInterceptor implements HttpInterceptor {

    spinner = inject(SpinnerService);
    sec = inject(SecurityService);
    http = inject(HttpClient);
    router = inject(Router);
    modal = inject(NgbModal);
    ns = inject(NotificationService);
    inactivityTimeout = inject(InactivityTimeoutService);

	reqCount = 0;

	intercept(
        req: HttpRequest<any>
        , next: HttpHandler
    ) {
		++this.reqCount;
		this.setSpinner();

		// Get the URL, with automatic base URL, if beginning with 'api/' or '/api/'
		const origUrl: string = req.url;
		let url = req.url.startsWith('api/') || req.url.startsWith('/api/')
			? document.getElementsByTagName('base')[0].href + req.url
			: req.url;
		url = url.replace('//api', '/api');
		
		// Add the UTC offset as a query string
		if (
			!origUrl.startsWith('http') 
			&& !origUrl.endsWith("Signature.png")
		) {
			const timeZone = Intl.DateTimeFormat().resolvedOptions === undefined
				? undefined
				: Intl.DateTimeFormat().resolvedOptions().timeZone;
			url = url 
				+ (url.includes('?') ? '&' : '?') 
				+ `userTimeZone=${encodeURIComponent(timeZone!)}&` 
				+ `utcOffset=${new Date().getTimezoneOffset() / -60}`;
		}
		// Set the default content type and URL
		let newReq = req.headers.get('Content-Type') || req.body instanceof FormData
			? req.clone({ url })
			: req.clone({ headers: req.headers.set('Content-Type', 'application/json'), url });

		// Get the auth token from the service.
		const authToken = localStorage.getItem('jwt') 
			? JSON.parse(localStorage?.getItem('jwt')!) 
			: null;

		// Add the auth token to the header, if we have one
		if (
			authToken 
			&& !origUrl.startsWith('http')
		) { newReq = newReq.clone({ headers: newReq.headers.set('Authorization', 'Bearer ' + authToken) }); }

		// Set new last access date
		this.sec.setLocalStorage('lastAccess', new Date());

		// Send cloned request with header to the next handler.
		return next.handle(newReq).pipe(
			catchError((err: HttpErrorResponse) => this.handleError(err, newReq.method)),
			finalize(() => {
				--this.reqCount;
				this.setSpinner();
			})
		);
	}

	/**
	 * This is our default error handling function throughout the application.
	 *
	 * @param err The error response.
	*/
	handleError(
		err: HttpErrorResponse
		, method: string = 'GET'
	) {
        switch (err.status) {
			case 401:
				{
                    // S2N-3249: the server refused a signed-in session (it timed out or was revoked
                    // there first). End it completely - server session, cookies, local state - and
                    // reload to login, rather than routing there inside a page whose state belongs
                    // to the dead session. Later 401s from requests already in flight have nothing
                    // left to do.
                    if (this.inactivityTimeout.isEndingSession) { break; }
                    if (this.sec.getToken() || this.sec.getUser()) {
                        this.inactivityTimeout.endSession();
                        break;
                    }
                    this.modal.dismissAll(); // Make sure all modals are closed since they are moving to the login screen
                    this.sec.setSecurity(null, null, null); // Clear out the user security
                    let redirectUrl: string | null = null;
                    const redirect = globalThis.location.href.replace(document.getElementsByTagName('base')[0].href, '');
                    if (decodeURIComponent(redirect).includes("login?redirectUrl=")) { break; }
                    if (
                        redirect !== null 
                        && redirect !== '' 
                        && redirect !== undefined
                    ) { redirectUrl = '/' + redirect; }	
                    if (redirectUrl == null || redirectUrl == "/login") { this.router.navigateByUrl(`/login`); }
                    else { this.router.navigateByUrl(`/login?redirectUrl=${encodeURIComponent(redirectUrl)}`); }
                    break; 
                }
			case 403: {
				// The body is usually the server's message string, but a JSON or empty body is possible.
				const body: string = typeof err.error === 'string' ? err.error : '';
				// S2N-3076: a refused WRITE from a View Only account -- whether the write guard's own
				// message ("Access denied. This account is view only.") or the permission filter's generic
				// one on an unmarked write action -- means the user pressed something the screen still
				// offered. They ARE allowed on the screen (every read on it succeeded), so toast and stay
				// put; navigating or reloading would throw a reader off the page for a click that did
				// nothing. A refused GET is different: that is a screen this profile may not open, and it
				// takes the landing-page path below like any other profile's 403.
				const isViewOnly = profileIsViewOnly(this.sec.getSecurity()?.permissionProfile);
				if (
					body.toLowerCase().includes('view only')
					|| (
						isViewOnly
						&& method.toUpperCase() !== 'GET'
					)
				) {
					this.ns.showError(body || 'Access denied. This account is view only.', { timeOut: 5000 });
					break;
				}
				this.ns.showError(err.error, { timeOut: 5000 });
				this.http.get('api/Account/GetUserInfo').subscribe((model: any) => {
					this.sec.setSecurity(this.sec.getToken(), model.user, model.security);
                    if (body.includes('Access denied. You have not been associated to this inventory')) {
                        setTimeout(() => globalThis.location.href = '/', 1500); // refresh the page to get the correct nav
                    } else {
                        // S2N-1420: land on a page this profile can actually open, and never
                        // navigate when we are already on it. Bouncing every 403 to '/' sent a
                        // Witness to the dashboard, which redirected them back to their own page,
                        // which re-issued the refused request — an endless access-denied loop.
                        const landing = this.sec.getLandingUrl();
                        if (!this.sec.isOnUrl(landing)) { this.router.navigateByUrl(landing); }
                    }
				});
				break;
			}
			case 404:
				this.ns.showError('That page does not exist.', { timeOut: 5000 });
				this.http.get('api/Account/GetUserInfo').subscribe((model: any) => {
					this.sec.setSecurity(this.sec.getToken(), model.user, model.security);
					this.router.navigateByUrl('/');
				});
				break;			
			case 466: // This can sometimes be used to indicate confirmation messages, so ignore here
				break;
			case 467: // The user does not have 2FA or inactivity timeout enabled
				this.ns.showError('2FA and Inactivity Timeout must be enabled to view PHI.', { timeOut: 5000 });
				this.modal.dismissAll(); // Make sure all modals are closed since they are moving to the splash screen
				this.router.navigateByUrl('/');
				break;
			case 499: // First time Inventory Setup custom redirect
				this.modal.dismissAll();
				{
					// S2N-1420: the product-setup wizard is operational work. A profile without CoreFeatures
					// (Witness) is refused there, so send it to its landing page instead of bouncing it
					// through an "Access Denied" on a route it can never open.
					const target = this.sec.hasAccess(o => !!o.coreFeatures)
						? err.error
						: this.sec.getLandingUrl();
					if (!this.sec.isOnUrl(target)) { this.router.navigateByUrl(target); }
				}
				break;
			case 500:
				this.ns.showError('We are aware of the current issue and are working hard on getting it fixed as soon as possible. ' 
					+ 'If this message persists for too long, please contact us. Sorry for the inconvenience.',
					{ timeOut: 5000 });
				break;
            case 504:
                this.ns.showError('Check your internet connection. If you keep getting this error, please contact us.');
                datadogLogs.logger.error(err?.message, undefined, err);
                break;
			default:
				datadogLogs.logger.error(err?.message, undefined, err); 
		}
		return throwError(() => err);
	}

	/**
	 * Show and hide the spinner, depending on whether or not a request
	 * is still out.
	*/
	setSpinner() {
		if (this.reqCount === 0) { this.spinner.hideSpinner(); } 
		else { this.spinner.showSpinner(); }
	}
}
